import React from "react";
import styled from "styled-components";

import { glow, Truncate, colorFromTheme } from "@examspark/common-ui";

interface ItemProps {
  selected?: boolean;
}

const FilterItemLabel = styled.div`
  flex: 1;
  min-width: 0;
`;
const FilterItemCount = styled.div`
  width: 3rem;
`;
const Count = styled.div`
  color: ${colorFromTheme("accent", "contrastTextColor")};
  background-color: ${colorFromTheme("accent")};
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  height: 2rem;
  margin: -0.5rem 0;
`;

const StyledItem = styled.label<ItemProps>`
  display: flex;
  gap: 1rem;
  padding: 0.7rem;
  border-top: solid 1px #ddd;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: #333;
  min-width: 0;

  &:first-child {
    border-top: none;
  }

  input {
    order: -1;
  }

  &:focus-within {
    ${glow("outline")}
  }

  ${({ selected }) => selected && `background-color: #f3f3fd;`}

  &:hover {
    background-color: #f7f7f7;
    ${({ selected }) => selected && `background-color: #f3f3fd;`}
  }
`;

const StyledInput = styled.input`
  &:focus-visible {
    ${glow("outline")}
  }
`;

const Item: React.FC<
  React.PropsWithChildren<{
    checked?: boolean;
    count?: number;
    onClick?: () => void;
    disabled?: boolean;
  }>
> = (props) => {
  const formattedCount = () => {
    if (!!props.count && Number(props.count) > 1000) {
      return (
        (Math.round((Number(props.count) / 1000) * 10) / 10).toString() + "k"
      );
    }

    return props.count;
  };

  return (
    <StyledItem selected={props.checked}>
      <FilterItemLabel>
        <Truncate>{props.children}</Truncate>
      </FilterItemLabel>
      <StyledInput
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={props.onClick}
      />
      <FilterItemCount>
        {!!props.count && <Count>{formattedCount()}</Count>}
      </FilterItemCount>
    </StyledItem>
  );
};

const StyledList = styled.div`
  border: solid 1px #ddd;
  border-radius: 0.5rem;

  & ${StyledItem}:first-child {
    border-radius: 0.5rem 0.5rem 0 0;
  }
  & ${StyledItem}:last-child {
    border-radius: 0 0 0.5rem 0.5rem;
  }
  & ${StyledItem}:first-child:last-child {
    border-radius: 0.5rem;
  }
`;

const FilterCheckboxList: React.FC<React.PropsWithChildren<unknown>> & {
  Item: typeof Item;
} = (props) => {
  return <StyledList>{props.children}</StyledList>;
};

FilterCheckboxList.Item = Item;

export default FilterCheckboxList;
